import React, { useState } from 'react';
import { X, Clock, Zap, Sparkles, Brain, Loader2, CreditCard } from 'lucide-react';
import { getAccelerationOptions, DELIVERY_TIMES } from '../utils/accelerationHelpers';

export default function ReportGenerationModal({
    isOpen,
    onClose,
    onConfirm,
    onTopUp,
    creditPacks = [],
    userTier,
    userCredits,
    isGenerating,
    isPaymentProcessing
}) {
    const [selectedReportType, setSelectedReportType] = useState(null);
    const [selectedAcceleration, setSelectedAcceleration] = useState(null);
    const [selectedAddons, setSelectedAddons] = useState([]);
    const [isFaqOpen, setIsFaqOpen] = useState(false);
    const [faqOpenIndex, setFaqOpenIndex] = useState(null);
    const [activeTab, setActiveTab] = useState("report");
    const [pendingTopUp, setPendingTopUp] = useState(null);

    if (!isOpen) return null;

    const normalizedTier = userTier?.toLowerCase() || 'free';
    const standardDeliveryHours = DELIVERY_TIMES[normalizedTier] || 9;
    const accelerationOptions = getAccelerationOptions(normalizedTier, userCredits);

    const reportTypes = [
        {
            id: 'basic',
            name: 'Basic Report',
            credits: 150,
            icon: Sparkles,
            description: 'Standard AI analysis with key insights',
            color: 'sky'
        },
        {
            id: 'advanced',
            name: 'Advanced Report',
            credits: 1000,
            icon: Brain,
            description: 'Deep reasoning audit with comprehensive analysis',
            color: 'purple'
        }
    ];

    const reportAddons = [
        {
            id: 'credit',
            name: 'Credit Report',
            credits: 6000,
            icon: CreditCard,
            description: 'Full credit analysis with detailed borrower insights',
            color: 'amber'
        }
    ];

    const addonsCredits = selectedAddons.reduce((sum, addonId) => {
        const addon = reportAddons.find(a => a.id === addonId);
        return sum + (addon?.credits || 0);
    }, 0);

    const handleConfirm = () => {
        if (!selectedReportType) return;

        // Prevent duplicate submissions if already generating
        if (isGenerating) {
            console.warn("Report generation already in progress, ignoring duplicate click");
            return;
        }

        onConfirm({
            reportType: selectedReportType,
            acceleration: selectedAcceleration,
            addons: selectedAddons,
            totalCredits: reportTypes.find(r => r.id === selectedReportType).credits + (selectedAcceleration?.credits || 0) + addonsCredits
        });
    };

    const selectedReport = reportTypes.find(r => r.id === selectedReportType);
    const totalCredits = (selectedReport?.credits || 0) + (selectedAcceleration?.credits || 0) + addonsCredits;
    const canAfford = userCredits >= totalCredits;
    const deliveryHours = selectedAcceleration?.hours || standardDeliveryHours;
    const creditPackOptions = creditPacks.length
        ? creditPacks.map((pack) => ({
            credits: Number(String(pack.credits).replace(/[^0-9]/g, "")),
            label: `Buy ${Number(String(pack.credits).replace(/[^0-9]/g, "")).toLocaleString()} Credits - ${pack.price || ""}`.trim(),
            sub: pack.discountPercentage ? `Save ${pack.discountPercentage}%` : "Instant Access.",
            featured: Boolean(pack.featured),
            lookupKey: pack.stripeLookupKey
        }))
        : [
            { credits: 2000, label: "Buy 2,000 Credits - $20", sub: "Instant Access.", lookupKey: "credits_2000" },
            { credits: 6000, label: "Buy 6,000 Credits - $50", sub: "(Best Seller)", featured: true, lookupKey: "credits_6000" },
            { credits: 30000, label: "Buy 30,000 Credits - $200", sub: "Save 33%.", lookupKey: "credits_30000" },
        ];

    return (
        <div className="fixed inset-0 z-[120] flex items-center justify-center dark:bg-slate-900/80 bg-slate-500/50 backdrop-blur-sm px-4">
            <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl max-w-2xl w-full shadow-2xl max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b dark:border-slate-800 border-slate-200 sticky top-0 dark:bg-slate-900 bg-white z-10">
                    <div>
                        <h3 className="text-xl font-bold dark:text-white text-slate-900">Generate AI Report</h3>
                        <p className="text-sm text-slate-400 mt-1">
                            Choose report type and delivery speed
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        disabled={isGenerating}
                        className="text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors disabled:opacity-50"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                    {/* Tabs */}
                    <div className="flex items-center border-b dark:border-slate-700 border-slate-200 mx-6">
                        <button
                            type="button"
                            onClick={() => setActiveTab("report")}
                            className={`flex-1 px-4 py-3 text-sm font-medium transition-all border-b-2 ${activeTab === "report"
                                ? "border-sky-500 dark:text-sky-400 text-sky-600"
                                : "border-transparent text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200"
                                }`}
                        >
                            Generate Report
                        </button>
                        <button
                            type="button"
                            onClick={() => setActiveTab("topup")}
                            className={`flex-1 px-4 py-3 text-sm font-medium transition-all border-b-2 ${activeTab === "topup"
                                ? "border-emerald-500 dark:text-emerald-400 text-emerald-600"
                                : "border-transparent text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200"
                                }`}
                        >
                            Top Up Credits
                        </button>
                    </div>

                    {activeTab === "report" && (
                        <>
                            {/* Step 1: Choose Report Type */}
                            <div>
                                <h4 className="text-sm font-semibold dark:text-white text-slate-900 mb-3 flex items-center gap-2">
                                    <span className="w-6 h-6 rounded-full dark:bg-sky-500/20 bg-sky-100 dark:text-sky-400 text-sky-600 text-xs flex items-center justify-center">1</span>
                                    Choose Report Type
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {reportTypes.map(report => {
                                        const Icon = report.icon;
                                        const isSelected = selectedReportType === report.id;
                                        const colorClasses = report.color === 'sky'
                                            ? 'border-sky-500/50 dark:bg-sky-500/10 bg-sky-50 shadow-[0_0_20px_rgba(56,189,248,0.2)]'
                                            : 'border-purple-500/50 dark:bg-purple-500/10 bg-purple-50 shadow-[0_0_20px_rgba(168,85,247,0.2)]';

                                        return (
                                            <button
                                                key={report.id}
                                                onClick={() => setSelectedReportType(report.id)}
                                                disabled={isGenerating}
                                                className={`p-4 rounded-xl border-2 transition-all text-left ${isSelected
                                                    ? colorClasses
                                                    : 'dark:border-slate-700 border-slate-200 dark:bg-slate-800/50 bg-white hover:border-slate-300 dark:hover:border-slate-600'
                                                    } disabled:opacity-50`}
                                            >
                                                <div className="flex items-start gap-3">
                                                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${report.color === 'sky' ? 'dark:bg-sky-500/20 bg-sky-100' : 'dark:bg-purple-500/20 bg-purple-100'
                                                        }`}>
                                                        <Icon className={`w-5 h-5 ${report.color === 'sky' ? 'dark:text-sky-400 text-sky-600' : 'dark:text-purple-400 text-purple-600'
                                                            }`} />
                                                    </div>
                                                    <div className="flex-1">
                                                        <p className="font-semibold dark:text-white text-slate-900">{report.name}</p>
                                                        <p className="text-xs text-slate-500 mt-1">{report.description}</p>
                                                        <p className="text-sm font-bold dark:text-sky-400 text-sky-600 mt-2">{report.credits} credits</p>
                                                    </div>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Step 2: Standard Delivery Time */}
                            {selectedReportType && (
                                <div className="border dark:border-amber-500/30 border-amber-200 rounded-xl p-4 bg-gradient-to-br from-amber-500/5 dark:via-slate-900/50 via-white to-white dark:to-slate-900/50">
                                    <div className="flex items-center gap-3 mb-3">
                                        <div className="w-10 h-10 rounded-xl dark:bg-amber-500/20 bg-amber-100 border dark:border-amber-400/30 border-amber-200 flex items-center justify-center">
                                            <Clock className="w-5 h-5 dark:text-amber-400 text-amber-600" />
                                        </div>
                                        <div>
                                            <p className="text-sm font-semibold dark:text-white text-slate-900">Standard Delivery Time</p>
                                            <p className="text-xs text-slate-500">Your {userTier} membership</p>
                                        </div>
                                    </div>
                                    <div className="text-center py-3 px-4 rounded-lg dark:bg-slate-950/50 bg-white border dark:border-slate-800 border-amber-100">
                                        <p className="text-2xl font-bold dark:text-amber-400 text-amber-600">{standardDeliveryHours} hours</p>
                                        <p className="text-xs text-slate-500 mt-1">
                                            Expected by: {new Date(Date.now() + standardDeliveryHours * 60 * 60 * 1000).toLocaleString('en-US', {
                                                month: 'short',
                                                day: 'numeric',
                                                hour: 'numeric',
                                                minute: '2-digit'
                                            })}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* Step 3: Acceleration Options */}
                            {selectedReportType && accelerationOptions.length > 0 && (
                                <div>
                                    <h4 className="text-sm font-semibold dark:text-white text-slate-900 mb-3 flex items-center gap-2">
                                        <span className="w-6 h-6 rounded-full dark:bg-sky-500/20 bg-sky-100 dark:text-sky-400 text-sky-600 text-xs flex items-center justify-center">2</span>
                                        Speed Up Delivery? (Optional)
                                    </h4>
                                    <div className="space-y-2">
                                        {/* Standard option */}
                                        <button
                                            onClick={() => setSelectedAcceleration(null)}
                                            disabled={isGenerating}
                                            className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all ${selectedAcceleration === null
                                                ? 'border-emerald-500/50 dark:bg-emerald-500/10 bg-emerald-50'
                                                : 'dark:border-slate-700 border-slate-200 dark:bg-slate-800/50 bg-white hover:border-slate-300 dark:hover:border-slate-600'
                                                } disabled:opacity-50`}
                                        >
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <p className="text-sm font-semibold dark:text-white text-slate-900">Standard Delivery</p>
                                                    <p className="text-xs text-slate-500 mt-0.5">
                                                        {standardDeliveryHours} hours • No extra cost
                                                    </p>
                                                    <p className="text-xs dark:text-amber-400 text-amber-600 mt-1">
                                                        ETA: {new Date(Date.now() + standardDeliveryHours * 60 * 60 * 1000).toLocaleString('en-US', {
                                                            month: 'short',
                                                            day: 'numeric',
                                                            hour: 'numeric',
                                                            minute: '2-digit'
                                                        })}
                                                    </p>
                                                </div>
                                                <div className="dark:text-emerald-400 text-emerald-600 text-xs font-semibold">FREE</div>
                                            </div>
                                        </button>

                                        {/* Acceleration options */}
                                        {accelerationOptions.map(option => (
                                            <button
                                                key={option.level}
                                                onClick={() => setSelectedAcceleration(option)}
                                                disabled={isGenerating || !option.canAfford}
                                                className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all ${selectedAcceleration?.level === option.level
                                                    ? 'border-sky-500/50 dark:bg-sky-500/10 bg-sky-50 shadow-[0_0_20px_rgba(56,189,248,0.15)]'
                                                    : option.canAfford
                                                        ? 'dark:border-slate-700 border-slate-200 dark:bg-slate-800/50 bg-white hover:border-slate-300 dark:hover:border-slate-600'
                                                        : 'dark:border-slate-700 border-slate-200 dark:bg-slate-800/50 bg-white opacity-50 cursor-not-allowed'
                                                    } disabled:opacity-50`}
                                            >
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <div className="flex items-center gap-2">
                                                            <Zap className="w-4 h-4 dark:text-sky-400 text-sky-600" />
                                                            <p className="text-sm font-semibold dark:text-white text-slate-900">{option.label}</p>
                                                        </div>
                                                        <p className="text-xs text-slate-500 mt-0.5">
                                                            Get it in <span className="dark:text-sky-400 text-sky-600 font-semibold">{option.hours}h</span> (save {option.timeSaved}h)
                                                        </p>
                                                        <p className="text-xs dark:text-sky-400 text-sky-600 mt-1">
                                                            ETA: {new Date(Date.now() + option.hours * 60 * 60 * 1000).toLocaleString('en-US', {
                                                                month: 'short',
                                                                day: 'numeric',
                                                                hour: 'numeric',
                                                                minute: '2-digit'
                                                            })}
                                                        </p>
                                                    </div>
                                                    <div className="text-right">
                                                        <p className="text-sm font-bold dark:text-sky-400 text-sky-600">+{option.credits}</p>
                                                        <p className="text-[10px] text-slate-500 uppercase tracking-wide">credits</p>
                                                    </div>
                                                </div>
                                                {!option.canAfford && (
                                                    <p className="text-[10px] text-red-500 mt-2">Insufficient credits</p>
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Add-ons Section */}
                            {selectedReportType && (
                                <div>
                                    <h4 className="text-sm font-semibold dark:text-white text-slate-900 mb-3">Add-ons (Optional)</h4>
                                    <div className="space-y-2">
                                        {reportAddons.map(addon => {
                                            const Icon = addon.icon;
                                            const isSelected = selectedAddons.includes(addon.id);
                                            const canAffordAddon = userCredits >= (totalCredits + (isSelected ? 0 : addon.credits));

                                            const toggleAddon = () => {
                                                if (isGenerating) return;
                                                if (isSelected) {
                                                    setSelectedAddons(prev => prev.filter(id => id !== addon.id));
                                                } else if (canAffordAddon) {
                                                    setSelectedAddons(prev => [...prev, addon.id]);
                                                }
                                            };

                                            return (
                                                <button
                                                    key={addon.id}
                                                    onClick={toggleAddon}
                                                    disabled={isGenerating || (!isSelected && !canAffordAddon)}
                                                    className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all ${isSelected
                                                        ? 'border-amber-500/50 dark:bg-amber-500/10 bg-amber-50 shadow-[0_0_20px_rgba(245,158,11,0.15)]'
                                                        : canAffordAddon
                                                            ? 'dark:border-slate-700 border-slate-200 dark:bg-slate-800/50 bg-white hover:border-slate-300 dark:hover:border-slate-600'
                                                            : 'dark:border-slate-700 border-slate-200 dark:bg-slate-800/50 bg-white opacity-50 cursor-not-allowed'
                                                        } disabled:opacity-50`}
                                                >
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-3">
                                                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isSelected ? 'dark:bg-amber-500/20 bg-amber-100' : 'dark:bg-slate-700 bg-slate-100'}`}>
                                                                <Icon className={`w-4 h-4 ${isSelected ? 'dark:text-amber-400 text-amber-600' : 'dark:text-slate-400 text-slate-500'}`} />
                                                            </div>
                                                            <div>
                                                                <p className="text-sm font-semibold dark:text-white text-slate-900">{addon.name}</p>
                                                                <p className="text-xs text-slate-500">{addon.description}</p>
                                                            </div>
                                                        </div>
                                                        <div className="text-right">
                                                            <p className="text-sm font-bold dark:text-amber-400 text-amber-600">+{addon.credits.toLocaleString()}</p>
                                                            <p className="text-[10px] text-slate-500 uppercase tracking-wide">credits</p>
                                                        </div>
                                                    </div>
                                                    {!isSelected && !canAffordAddon && (
                                                        <p className="text-[10px] text-red-500 mt-2">Insufficient credits</p>
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Summary */}
                            {selectedReportType && (
                                <div className="border-t dark:border-slate-800 border-slate-200 pt-4 space-y-3">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-slate-500">Report Type:</span>
                                        <span className="text-sm font-semibold dark:text-white text-slate-900">{selectedReport?.name}</span>
                                    </div>
                                    {selectedAddons.length > 0 && (
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm text-slate-500">Add-ons:</span>
                                            <span className="text-sm font-semibold dark:text-amber-400 text-amber-600">
                                                {selectedAddons.map(id => reportAddons.find(a => a.id === id)?.name).join(', ')}
                                            </span>
                                        </div>
                                    )}
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-slate-500">Delivery Time:</span>
                                        <span className="text-sm font-semibold dark:text-amber-400 text-amber-600">{deliveryHours} hours</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-slate-500">Total Cost:</span>
                                        <span className="text-lg font-bold dark:text-white text-slate-900">{totalCredits.toLocaleString()} credits</span>
                                    </div>
                                    <div className="flex items-center justify-between text-xs">
                                        <span className="text-slate-500">Your balance:</span>
                                        <span className="text-slate-400">{userCredits.toLocaleString()} credits</span>
                                    </div>
                                    {!canAfford && (
                                        <div className="p-3 rounded-lg dark:bg-red-500/10 bg-red-50 border dark:border-red-500/30 border-red-200">
                                            <p className="text-sm dark:text-red-300 text-red-700">
                                                ⚠️ You need <span className="font-bold">{totalCredits - userCredits}</span> more credits
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="border-t dark:border-slate-800 border-slate-200 pt-4">
                                <button
                                    type="button"
                                    onClick={() => setIsFaqOpen((prev) => !prev)}
                                    className="w-full flex items-center justify-between gap-3 text-left"
                                >
                                    <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">FAQs</p>
                                    <span className={`transition-transform duration-200 ${isFaqOpen ? "rotate-90 dark:text-sky-400 text-sky-600" : "text-slate-400"}`}>›</span>
                                </button>
                                {isFaqOpen && (
                                    <div className="grid grid-cols-2 gap-3 mt-3">
                                        {[
                                            { question: "Question 1", answer: "Answer 1" },
                                            { question: "Question 2", answer: "Answer 2" },
                                            { question: "Question 3", answer: "Answer 3" },
                                            { question: "Question 4", answer: "Answer 4" },
                                        ].map((faq, idx) => {
                                            const isOpen = faqOpenIndex === idx;
                                            return (
                                                <div
                                                    key={faq.question}
                                                    className="rounded-xl border dark:border-slate-800 border-slate-200 dark:bg-slate-900/70 bg-white shadow-md shadow-black/5"
                                                >
                                                    <button
                                                        type="button"
                                                        onClick={() => setFaqOpenIndex(isOpen ? null : idx)}
                                                        className="w-full flex items-center justify-between gap-2 px-3 py-3 text-left"
                                                    >
                                                        <span className="text-sm font-semibold dark:text-white text-slate-900">{faq.question}</span>
                                                        <span className={`text-slate-400 transition-transform duration-200 ${isOpen ? "rotate-90 dark:text-sky-400 text-sky-600" : ""}`}>›</span>
                                                    </button>
                                                    {isOpen && (
                                                        <div className="px-3 pb-3">
                                                            <p className="text-slate-500 leading-relaxed text-sm">{faq.answer}</p>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                </div>

                {activeTab === "topup" && (
                    <div className="p-6 pt-2">
                        <div className="text-center mb-6">
                            <span className="inline-block px-3 py-1 dark:bg-emerald-500/10 bg-emerald-100 dark:text-emerald-400 text-emerald-700 text-xs font-semibold rounded-full mb-2">
                                Instant Credit Access
                            </span>
                            <h4 className="text-lg font-bold dark:text-white text-slate-900">Select a Credit Pack</h4>
                            <p className="text-sm text-slate-500 mt-1">Unlock more underwriting power immediately.</p>
                        </div>

                        <div className="grid md:grid-cols-3 gap-4">
                            {creditPackOptions.map((pack) => {
                                const isSelected = pendingTopUp?.credits === pack.credits;
                                return (
                                    <button
                                        key={pack.credits}
                                        className={`relative text-left rounded-2xl p-5 transition-all duration-200 group ${isSelected
                                            ? "dark:bg-emerald-500/10 bg-emerald-50 border-2 border-emerald-500 shadow-[0_0_20px_rgba(16,185,129,0.2)]"
                                            : "dark:bg-slate-800/50 bg-white border dark:border-slate-700 border-slate-200 hover:border-emerald-500/50 dark:hover:bg-slate-800 hover:bg-slate-50 hover:-translate-y-1"
                                            }`}
                                        onClick={() => setPendingTopUp(pack)}
                                        disabled={isPaymentProcessing}
                                    >
                                        {pack.featured && (
                                            <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-500 text-slate-900 text-[10px] font-bold px-2 py-0.5 rounded-full shadow-lg">
                                                POPULAR
                                            </span>
                                        )}
                                        <div className="flex flex-col h-full justify-between">
                                            <div>
                                                <p className={`text-sm font-semibold ${isSelected ? 'dark:text-emerald-400 text-emerald-600' : 'dark:text-slate-200 text-slate-700 group-hover:text-emerald-600 dark:group-hover:text-emerald-400'}`}>
                                                    {Number(pack.credits).toLocaleString()} Credits
                                                </p>
                                                <p className="text-xl font-bold dark:text-white text-slate-900 mt-1">{pack.label.split('-')[1]?.trim()}</p>
                                            </div>
                                            <div className="mt-4 pt-4 border-t dark:border-slate-700/50 border-slate-100">
                                                <p className="text-xs text-slate-500">{pack.sub}</p>
                                            </div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Footer */}
                {/* Footer */}
                <div className="flex gap-3 p-6 border-t dark:border-slate-800 border-slate-200 sticky bottom-0 dark:bg-slate-900 bg-white z-10 transition-all duration-300">
                    <button
                        onClick={onClose}
                        disabled={isGenerating || isPaymentProcessing}
                        className="flex-1 px-4 py-2.5 rounded-xl border dark:border-slate-700 border-slate-200 dark:text-slate-300 text-slate-600 font-medium hover:bg-slate-100 dark:hover:bg-slate-800 dark:hover:text-white transition-colors disabled:opacity-50"
                    >
                        Cancel
                    </button>

                    {activeTab === "report" && (
                        !selectedReportType ? (
                            <button
                                disabled
                                className="flex-[2] px-4 py-2.5 rounded-xl dark:bg-slate-800 bg-slate-100 text-slate-500 font-medium cursor-not-allowed border dark:border-slate-700 border-slate-200"
                            >
                                Select Report Type to Continue
                            </button>
                        ) : !canAfford ? (
                            <button
                                onClick={() => setActiveTab("topup")}
                                className="flex-[2] px-4 py-2.5 rounded-xl bg-emerald-600 text-white font-bold hover:bg-emerald-500 transition-all shadow-lg shadow-emerald-900/20"
                            >
                                Insufficient Credits - Buy More
                            </button>
                        ) : (
                            <button
                                onClick={handleConfirm}
                                disabled={isGenerating}
                                className="flex-[2] px-4 py-2.5 rounded-xl bg-sky-600 text-white font-bold hover:bg-sky-500 transition-all shadow-lg shadow-sky-900/20 disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {isGenerating && <Loader2 className="w-5 h-5 animate-spin" />}
                                {isGenerating ? 'Analyzing Case...' : `Generate Report • ${totalCredits} credits`}
                            </button>
                        )
                    )}

                    {activeTab === "topup" && (
                        <button
                            onClick={() => pendingTopUp && onTopUp && onTopUp({ lookupKey: pendingTopUp.lookupKey, credits: pendingTopUp.credits })}
                            disabled={!pendingTopUp || isPaymentProcessing}
                            className={`flex-[2] px-4 py-2.5 rounded-xl font-bold transition-all shadow-lg flex items-center justify-center gap-2 ${!pendingTopUp
                                ? "dark:bg-slate-800 bg-slate-100 text-slate-500 cursor-not-allowed border dark:border-slate-700 border-slate-200"
                                : "bg-emerald-600 text-white hover:bg-emerald-500 shadow-emerald-900/20"
                                }`}
                        >
                            {isPaymentProcessing && <Loader2 className="w-5 h-5 animate-spin" />}
                            {isPaymentProcessing
                                ? "Processing..."
                                : pendingTopUp
                                    ? `Proceed to Checkout • $${pendingTopUp.label.split('- $')[1] || pendingTopUp.label}`
                                    : "Select a Credit Pack"}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
